/**
 * HiddenMessageHandler - Hidden message creation for internal operations
 *
 * Creates hidden user/AI message pairs for internal operations like search
 * polling that shouldn't appear in the chat UI.
 *
 * Extracted from AgentCoreAdapter._sendHiddenMessage()
 *
 * @example
 * const handler = new HiddenMessageHandler({
 *   sessionManager,
 *   messageManager,
 *   onInvoke: (sessionId, content, aiMessage, isHidden) =>
 *     this._invokeAgentCoreStreaming(sessionId, content, aiMessage, isHidden),
 *   onError: (error, sessionId, content) =>
 *     console.error('Hidden message failed:', error),
 *   getCurrentUserId: () => authStore.currentUserId
 * });
 *
 * await handler.sendHiddenMessage('session-1', 'search query', 'user-123');
 */

class HiddenMessageHandler {
  /**
   * @param {Object} options
   * @param {Object} options.sessionManager - Session state manager
   * @param {Object} options.messageManager - Message state manager
   * @param {Function} options.onInvoke - Callback to invoke agent with (sessionId, content, aiMessage, isHidden)
   * @param {Function} [options.onError] - Optional callback for error tracking (error, sessionId, content)
   */
  constructor({ sessionManager, messageManager, onInvoke, onError } = {}) {
    if (!sessionManager) {
      throw new Error('sessionManager is required');
    }
    if (!messageManager) {
      throw new Error('messageManager is required');
    }
    if (!onInvoke || typeof onInvoke !== 'function') {
      throw new Error('onInvoke callback is required');
    }

    this._sessionManager = sessionManager;
    this._messageManager = messageManager;
    this._onInvoke = onInvoke;
    this._onError = typeof onError === 'function' ? onError : null;
  }

  /**
   * Send a hidden message that won't appear in chat UI.
   * Creates hidden user message + AI message pair for internal operations.
   *
   * The onInvoke call is fire-and-forget — SearchPollCoordinator manages its own
   * 5-minute lifecycle timeout, so no artificial timeout is needed here.
   *
   * @param {string} sessionId - Session to send message to
   * @param {string} content - Message content
   * @param {string} userId - Current user ID for authorization check
   * @returns {Promise<Object>} The created AI message (in 'thinking' phase, will be updated by onInvoke callback)
   * @throws {Error} If session not found or user not authorized
   * @param {Object} [options]
   * @param {boolean} [options.awaitResponse=false] - Resolve only once the agent turn has
   *   finished, and reject if it failed. Default is fire-and-forget.
   */
  async sendHiddenMessage(sessionId, content, userId, { awaitResponse = false } = {}) {
    if (import.meta.env.DEV) console.log('👻 HIDDEN_MSG: Sending hidden message:', content);

    // Verify session exists AND user is authorized
    const session = this._sessionManager.getById(sessionId);
    if (!session) {
      throw new Error(`Session not found: ${sessionId}`);
    }
    if (session.userId && userId && session.userId !== userId) {
      throw new Error('Unauthorized: session belongs to different user');
    }

    const sessionMessages = this._messageManager.getForSession(sessionId);

    // Create hidden user message (won't be displayed in UI)
    const hiddenUserMessage = {
      id: crypto.randomUUID(),
      type: 'user',
      content,
      timestamp: new Date(),
      hidden: true
    };

    // Create AI message manually (without notification) for hidden polling messages
    const aiMessage = {
      id: crypto.randomUUID(),
      type: 'ai',
      sessionId,
      content: 'Thinking...',
      timestamp: new Date(),
      hidden: true,
      phase: 'thinking',
      isComplete: false
    };

    // Batch message creation into single operation to reduce race conditions
    sessionMessages.push(hiddenUserMessage, aiMessage);
    this._messageManager.setForSession(sessionId, sessionMessages);

    // Store message IDs for potential cleanup
    const createdMessageIds = new Set([hiddenUserMessage.id, aiMessage.id]);

    // Call agent API (mark as hidden message to preserve search state).
    // Streaming completes via the onComplete callback; callers that need the outcome
    // pass awaitResponse and get the failure as a rejection.
    const invocation = this._onInvoke(sessionId, content, aiMessage, true).catch((error) => {
      console.error('❌ HIDDEN_MSG: Failed to get response:', error);

      if (this._onError) {
        try {
          this._onError(error, sessionId, content);
        } catch (callbackError) {
          if (import.meta.env.DEV) console.warn('⚠️ HIDDEN_MSG: Error callback failed:', callbackError);
        }
      }

      // Cleanup orphaned hidden messages on real errors (network failure, etc.)
      if (this._sessionManager.has(sessionId)) {
        try {
          const currentMessages = this._messageManager.getForSession(sessionId);
          const cleanedMessages = currentMessages.filter(
            (msg) => !createdMessageIds.has(msg.id)
          );
          this._messageManager.setForSession(sessionId, cleanedMessages);
        } catch (cleanupError) {
          console.error('❌ HIDDEN_MSG: Cleanup failed:', cleanupError);
        }
      }

      throw error;
    });

    if (awaitResponse) {
      await invocation;
    } else {
      invocation.catch(() => {});
    }

    return aiMessage;
  }
}

export default HiddenMessageHandler;
